/** Format large amounts in a compact way: 30 M €, 425 k €, etc. */
export function formatCompactAmount(amount: number): string {
  const abs = Math.abs(amount);
  if (abs >= 1_000_000_000) {
    return `${(amount / 1_000_000_000).toFixed(1).replace(/\.0$/, "")} Md €`;
  }
  if (abs >= 1_000_000) {
    return `${(amount / 1_000_000).toFixed(1).replace(/\.0$/, "")} M €`;
  }
  if (abs >= 1_000) {
    return `${(amount / 1_000).toFixed(0)} k €`;
  }
  return `${amount} €`;
}

/** Format a price in EUR with french locale (e.g. 12,50 €). */
export function formatPrice(amount: number): string {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(amount);
}

/** Format a number with french locale (e.g. 10 000). */
export function formatNumber(count: number): string {
  return new Intl.NumberFormat("fr-FR").format(count);
}

/** Compute YoY percentage between two values */
export function computeYoy(
  current: number | null | undefined,
  previous: number | null | undefined,
): number | null {
  if (current == null || previous == null || previous === 0) return null;
  return ((current - previous) / Math.abs(previous)) * 100;
}

/** Compact number formatting using Intl (1.2K, 3.4M…) in the current locale. */
export function formatCompactNumber(value: number): string {
  return Intl.NumberFormat(undefined, {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);
}

/**
 * Format a ratio or percentage:
 *  - value <= 1 is treated as a 0-1 ratio (0.42 → 42.0%)
 *  - value > 1 is treated as already expressed in percent (42 → 42.0%)
 */
export function formatPercentage(value: number): string {
  const normalized = value > 1 ? value : value * 100;
  return `${normalized.toFixed(1)}%`;
}

/** Format a duration in seconds as "Ns" or "Nm SSs". */
export function formatDuration(seconds: number): string {
  const totalSeconds = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(totalSeconds / 60);
  const remaining = totalSeconds % 60;
  if (minutes === 0) return `${remaining}s`;
  return `${minutes}m ${remaining.toString().padStart(2, "0")}s`;
}

/**
 * A duration in words, in the app language: "30 secondes", "1 minute". A
 * minute or more is rounded up to whole minutes — for a wait, saying less
 * than the real time would only send the user into another refusal.
 *
 * @param seconds The duration, in seconds.
 * @param language The app language (`currentLanguage`), never the browser's.
 */
export function formatDurationLong(seconds: number, language: string): string {
  const totalSeconds = Math.max(0, Math.ceil(seconds));
  const [value, unit] =
    totalSeconds < 60
      ? [totalSeconds, "second"]
      : [Math.ceil(totalSeconds / 60), "minute"];
  return new Intl.NumberFormat(language, {
    style: "unit",
    unit,
    unitDisplay: "long",
  }).format(value);
}

const DATE_LOCALES: Record<string, string> = {
  fr: "fr-FR",
  en: "en-US",
};

/**
 * Locale-aware long date ("January 4, 2024" / "4 janvier 2024") of a calendar
 * date (a registry date, a round's date). A date-only value parses as UTC
 * midnight: it is read in UTC, or it shows the day before west of UTC.
 */
export function formatDateLong(
  input: string | Date,
  language: string,
): string {
  const date = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(date.getTime())) {
    return typeof input === "string" ? input : "";
  }
  return date.toLocaleDateString(DATE_LOCALES[language] ?? DATE_LOCALES.en, {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}

/**
 * Locale-aware day and month ("December 31" / "31 décembre") of a calendar
 * date, read in UTC as {@link formatDateLong} is.
 */
export function formatDayMonth(input: string | Date, language: string): string {
  const date = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(date.getTime())) {
    return typeof input === "string" ? input : "";
  }
  return date.toLocaleDateString(DATE_LOCALES[language] ?? DATE_LOCALES.en, {
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}

/**
 * Locale-aware short date ("Jan 2024" / "janv. 2024") of a calendar month or
 * date, read in UTC as {@link formatDateLong} is.
 */
export function formatDateShortMonth(
  input: string | Date,
  language: string,
): string {
  const date = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(date.getTime())) {
    return typeof input === "string" ? input : "";
  }
  return date.toLocaleDateString(DATE_LOCALES[language] ?? DATE_LOCALES.en, {
    year: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

/** Locale-aware numeric date ("Jan 4, 2024" / "4 janv. 2024"). Empty when null. */
export function formatDateNumeric(
  date: Date | string | null | undefined,
  language: string,
): string {
  if (!date) return "";
  return new Intl.DateTimeFormat(language, {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(date));
}

/** Locale-aware numeric date + time ("Jan 4, 2024, 14:05"). */
export function formatDateTimeNumeric(
  date: Date | string,
  language: string,
): string {
  return new Intl.DateTimeFormat(language, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(date));
}

/** Locale-aware currency amount. Currency code is case-insensitive. */
export function formatCurrency(
  amount: number,
  currency: string,
  language: string,
  options?: Intl.NumberFormatOptions,
): string {
  return new Intl.NumberFormat(language, {
    style: "currency",
    currency: currency.toUpperCase(),
    ...options,
  }).format(amount);
}

/** Initials from a user's first/last name, falling back to the email or "?". */
export function userInitials(
  user:
    | {
        firstName?: string | null;
        lastName?: string | null;
        email?: string | null;
      }
    | null
    | undefined,
): string {
  if (!user) return "?";
  const initials = `${user.firstName?.[0] ?? ""}${
    user.lastName?.[0] ?? ""
  }`.toUpperCase();
  return initials || user.email?.[0]?.toUpperCase() || "?";
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  EUR: "€",
  USD: "$",
  GBP: "£",
  CHF: "CHF",
  JPY: "¥",
};

/**
 * Compact amount formatting with an arbitrary currency (EUR default).
 * Used by funding rounds where the currency may vary per round.
 */
export function formatCompactAmountForCurrency(
  amount: number,
  currency: string | null | undefined,
): string {
  const normalized = (currency ?? "EUR").toUpperCase();
  const symbol = CURRENCY_SYMBOLS[normalized] ?? normalized;
  const abs = Math.abs(amount);
  const body = (() => {
    if (abs >= 1_000_000_000) {
      return `${(amount / 1_000_000_000).toFixed(1).replace(/\.0$/, "")}B`;
    }
    if (abs >= 1_000_000) {
      return `${(amount / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
    }
    if (abs >= 1_000) return `${(amount / 1_000).toFixed(0)}K`;
    return `${amount}`;
  })();
  return symbol.length > 1 ? `${body} ${symbol}` : `${symbol}${body}`;
}
